import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STARTERS } from '@/mocks/composer';
import { composerSubmitPath } from '../composerGuide';

export default function Hero({ logged }: { logged: boolean }) {
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [prompt, setPrompt] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = useCallback(() => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    navigate(composerSubmitPath(trimmed, logged));
  }, [prompt, navigate, logged]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <section className="relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(74,213,202,0.16), transparent)' }}
      />
      <div
        className="absolute inset-0 dot-grid opacity-60"
        style={{
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%)',
        }}
      />
      <div className="relative max-w-[1040px] mx-auto px-6 pt-20 md:pt-28 pb-16 flex flex-col items-center text-center">
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-textp font-semibold max-w-[720px]">
          What do you want to build?
        </h1>
        <p className="mt-4 text-base sm:text-lg text-texts leading-relaxed max-w-lg">
          Describe the app. Sourcetape is a launchpad: you get a working artifact and train it so it stays good.
        </p>

        <div
          data-testid="home-composer-form"
          className={`w-full max-w-2xl mt-10 bg-surface border rounded-xl text-left transition-all duration-200 ${
            isFocused ? 'border-accent shadow-sm' : 'border-border'
          }`}
        >
          <textarea
            ref={textareaRef}
            data-testid="home-composer-input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            placeholder="An admin dashboard to manage my customers..."
            rows={3}
            className="w-full bg-transparent px-5 pt-4 pb-2 text-base text-textp placeholder:text-texts outline-none resize-none"
          />
          <div className="flex items-center justify-between px-3 pb-3">
            <span className="text-xs text-texts hidden sm:block">Press Enter to build</span>
            <button
              type="button"
              data-testid="home-composer-submit"
              onClick={handleSubmit}
              disabled={!prompt.trim()}
              className="flex items-center gap-1.5 bg-accent hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed text-accent-text text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              <span>Build</span>
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-6 flex-wrap justify-center">
          {STARTERS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                setPrompt(s.outcome);
                textareaRef.current?.focus();
              }}
              className="text-sm text-texts hover:text-textp bg-surface hover:bg-surface2 border border-border hover:border-accent/50 px-4 py-1.5 rounded-full transition-colors"
            >
              {s.label}
            </button>
          ))}
        </div>
        <a href="#gallery" className="mt-4 text-sm text-texts hover:text-textp">
          See apps like this
        </a>
      </div>
    </section>
  );
}
